import {compact} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {ProcessesFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SchemaTag} from 'folder-items/FileConstants';
import {
  ProcessExecutionType,
  ProcessPlatformFeatureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {processTableInventoryShortcutsActionFunction} from 'inventory/processesCustomInventoryActionFunctions';
import {ClientId} from 'notebook/ELNConstants';
import addSamplesFromStructuredTable from 'notebook/sample-propagation/addSamplesFromStructuredTable';
import addSamplesFromWorklist from 'notebook/sample-propagation/addSamplesFromWorklist';
import {addToWorklistActionFunction} from 'notebook/structured-tables/action-functions/addToWorklistActionFunction';
import {downloadCSVActionFunction} from 'notebook/structured-tables/action-functions/downloadCSVActionFunction';
import {exportAuditLogsActionFunction} from 'notebook/structured-tables/action-functions/exportAuditLogsActionFunction';
import {printBarcodesActionFunction} from 'notebook/structured-tables/action-functions/printBarcodesActionFunction';
import {refreshTableActionFunction} from 'notebook/structured-tables/action-functions/refreshTableActionFunction';
import {submitProcessOutputsActionFunction} from 'notebook/structured-tables/action-functions/submitProcessOutputsActionFunction';
import submitProcessOutputsRowsActionFunction from 'notebook/structured-tables/action-functions/submitProcessOutputsRowsActionFunction';
import makeToggleEditModeActionFunction from 'notebook/structured-tables/action-functions/toggle-edit-mode/makeToggleEditModeActionFunction';
import {isProcessTaskStructuredTableColumn} from 'notebook/structured-tables/columns/TypePredicates';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {getSpreadsheetTypeForColumn} from 'notebook/structured-tables/spreadsheet-import/getSpreadsheetColumnTypes';
import importRowsFromSpreadsheet from 'notebook/structured-tables/spreadsheet-import/importRowsFromSpreadsheet';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  ActionFunctions,
  BaseStructuredTable,
  LastSyncedUpstreamValue,
  PrimarySample,
  PrimarySampleLookupType,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableAddFromSourceType,
  StructuredTableAddOperation,
  StructuredTableColumn,
  StructuredTableColumnSummary,
  StructuredTableKind,
  StructuredTableRow,
  StructuredTableSchema,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {Cell, Table, TableColumnType} from 'notebook/tables/TableConstants';
import {
  getProcessOutputIdFromTypedValue,
  getProcessTaskIdFromCell,
} from 'notebook/tables/typed-values/DataTypes';
import {
  PROCESS_WIDER_COLUMN_WIDTH,
  ProcessOutputSchema,
  ProcessTableLocation,
} from 'processes/ProcessConstants';
import {
  canEditSchemaFieldValues,
  getProcessPlatformFeatureConfig,
} from 'processes/processes-platform/FeatureOptions';
import findProcessRelatedSchemaId from 'processes/tables/findProcessRelatedSchemaId';
import {makeOutputIdCell, makeTagValueCell, makeTaskIdCell} from 'processes/tables/MakeProcessCellUtils';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import APIUtil from 'util/APIUtil';
import {WORKLIST_FILL_LOCATION} from 'worklists/WorklistConstants';

import {getProcessTaskIdFromTypedValue} from '../../notebook/tables/typed-values/DataTypes';
import archiveOutputsActionFunction from './action-functions/archiveOutputsActionFunction';
import {createProcessActionFunction} from './action-functions/createProcessActionFunction';
import {createRequestV2SubmissionActionFunction} from './action-functions/createRequestV2SubmissionActionFunction';
import openInDashboardActionFunction from './action-functions/openInDashboardActionFunction';
import {openNextTaskGroupActionFunction} from './action-functions/openNextTaskGroupActionFunction';
import taskFinderActionFunction from './action-functions/taskFinderActionFunction';
import getColumnSummariesForProcessRelatedTable from './getColumnSummariesForProcessRelatedTable';
import ProcessOutputRowUpdater from './ProcessOutputRowUpdater';

/**
 * Returns the first process output schema in the given list of schemas, or null if none exists.
 */
export function getOutputSchema(schemas: Array<StructuredTableSchema>): ProcessOutputSchema | null {
  return (
    schemas.find((schema): schema is ProcessOutputSchema => APIUtil.isProcessOutputSchema(schema.id)) ?? null
  );
}

export interface ProcessOutputStructuredTableMetadata {
  tableLocation?: ProcessTableLocation;
  processId?: string;
  taskId?: string;
  isReadOnly?: boolean;
  disallowAddRows?: boolean;
}
export interface ProcessOutputStructuredTableRowMetadata {}

export type GenericProcessOutputStructuredTable<TIds> = BaseStructuredTable<
  ProcessOutputStructuredTableMetadata,
  ProcessOutputStructuredTableRowMetadata,
  TIds
>;

export type ProcessOutputStructuredTable = GenericProcessOutputStructuredTable<ClientId>;

// An "unpersisted" process output is one that hasn't yet been saved in our DB. So it doesn't have an ID.
interface UnpersistedProcessOutputForTableCreation {
  task: {id: string; humanId: string};
  fields: Array<SchemaTag>;
}

interface PersistedProcessOutputForTableCreation extends UnpersistedProcessOutputForTableCreation {
  id: string;
  humanId: string;
}

type ProcessOutputForTableCreation =
  | PersistedProcessOutputForTableCreation
  | UnpersistedProcessOutputForTableCreation;

function isPersistedOutputForTableCreation(
  output: ProcessOutputForTableCreation
): output is PersistedProcessOutputForTableCreation {
  return (output as PersistedProcessOutputForTableCreation).id != null;
}

export default class ProcessOutputStructuredTableAdapter extends GenericStructuredTableAdapter<
  ProcessOutputStructuredTableMetadata,
  ProcessOutputStructuredTableRowMetadata
> {
  static getAdapter(): ProcessOutputStructuredTableAdapter {
    return getStructuredTableAdapters()[
      StructuredTableKind.PROCESS_OUTPUT
    ] as ProcessOutputStructuredTableAdapter;
  }

  static makeCellFromOutput(output: ProcessOutputForTableCreation, column: StructuredTableColumn): Cell {
    switch (column.columnType) {
      case TableColumnType.PROCESS_OUTPUT:
        if (!isPersistedOutputForTableCreation(output)) {
          return {text: ''};
        }
        return makeOutputIdCell(output);
      case TableColumnType.PROCESS_TASK:
        return makeTaskIdCell({id: output.task.id, humanId: output.task.humanId});
      case TableColumnType.TAG_VALUE:
        return makeTagValueCell(output, column);
      default:
        assert(false, `Unexpected column type found: ${column.columnType}`);
    }
  }
  static makeRowFromOutput(
    output: ProcessOutputForTableCreation,
    columns: Array<StructuredTableColumn>
  ): {cells: Array<Cell>} {
    return {
      cells: columns.map((column) => ProcessOutputStructuredTableAdapter.makeCellFromOutput(output, column)),
    };
  }

  static optimisticallyMakeLastSyncedUpstreamValuesFromOutput(
    output: ProcessOutputForTableCreation,
    columns: Array<StructuredTableColumn>
  ): Array<LastSyncedUpstreamValue> {
    return columns.map((column) => ({
      columnGroup: null,
      columnType: column.columnType,
      fieldId: column.schemaFieldApiId ?? null,
      columnProvenanceUuid: column.provenanceUuid ?? null,
      value: ProcessOutputStructuredTableAdapter.makeCellFromOutput(output, column).typedValue ?? null,
      submittedAt: new Date().toISOString(),
    }));
  }

  getDisplayName(
    _: StructuredTable<ProcessOutputStructuredTableMetadata, ProcessOutputStructuredTableRowMetadata>
  ): string {
    return 'Outputs';
  }

  getBlankRowMetadata(
    tableMetadata: ProcessOutputStructuredTableMetadata
  ): ProcessOutputStructuredTableRowMetadata {
    return {};
  }

  _isTaskOrOutputColumnIncluded(
    columnType: TableColumnType,
    metadata: ProcessOutputStructuredTableMetadata
  ): boolean {
    const doesTableLocationIncludeColumn =
      metadata.tableLocation !== ProcessTableLocation.CREATE_MODAL &&
      metadata.tableLocation !== ProcessTableLocation.FIELD_MAPPING_MODAL;
    return doesTableLocationIncludeColumn;
  }

  _prepareColumn(
    columnType: TableColumnType,
    metadata: ProcessOutputStructuredTableMetadata
  ): StructuredTableColumn | null {
    switch (columnType) {
      case TableColumnType.PROCESS_TASK:
        return this._isTaskOrOutputColumnIncluded(columnType, metadata)
          ? makeStructuredTableColumn({
              columnType,
              name: 'Task ID',
              isRequired: true,
            })
          : null;
      case TableColumnType.PROCESS_OUTPUT:
        return this._isTaskOrOutputColumnIncluded(columnType, metadata)
          ? makeStructuredTableColumn({
              columnType,
              name: 'ID',
              isReadOnly: true,
              width: PROCESS_WIDER_COLUMN_WIDTH,
            })
          : null;
      default:
        assert(false, `Unhandled task table column type: ${columnType}`);
    }
  }

  // Keep in sync with create_empty_output_table_from_schema.py
  _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: ProcessOutputStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const outputAndTaskColumns = [TableColumnType.PROCESS_OUTPUT, TableColumnType.PROCESS_TASK];
    if (
      metadata.tableLocation &&
      metadata.tableLocation === ProcessTableLocation.REQUEST_V2_SUBMISSION_DETAILS_PAGE
    ) {
      outputAndTaskColumns.reverse();
    }
    return compact(
      outputAndTaskColumns.map((columnType) => this._prepareColumn(columnType, metadata))
    ).concat(
      super._getAllSupportedColumns({
        entryId,
        metadata,
        schemas,
        selectorsById,
        structuredTableInsertionContext,
      })
    );
  }

  injectFrontEndMetadata({
    column,
    schemas,
    tableMetadata,
  }: {
    column: StructuredTableColumn;
    schemas: Array<SchemaAndColumnGroup>;
    tableMetadata: ProcessOutputStructuredTableMetadata;
  }): StructuredTableColumn {
    if (isProcessTaskStructuredTableColumn(column)) {
      return {
        ...column,
        frontEndMetadata: {schemaId: findProcessRelatedSchemaId(schemas.map(({id}) => id))},
      };
    }
    return column;
  }

  _getIndexOfRequiredColumn(
    columns: Array<StructuredTableColumn>,
    columnType: TableColumnType,
    devFacingColumnName: string
  ): number {
    const colIndex = columns.findIndex((col) => col.columnType === columnType);
    assert(colIndex !== -1, `Process output table must have a ${devFacingColumnName} column`, {
      columns,
      columnType,
    });
    return colIndex;
  }

  _getOutputColumnIndex(columns: Array<StructuredTableColumn>): number {
    return this._getIndexOfRequiredColumn(columns, TableColumnType.PROCESS_OUTPUT, 'output');
  }

  _getTaskColumnIndex(columns: Array<StructuredTableColumn>): number {
    return this._getIndexOfRequiredColumn(columns, TableColumnType.PROCESS_TASK, 'task');
  }

  _shouldAllowAddRows(processOutputTable: ProcessOutputStructuredTable): boolean {
    const outputSchema = getOutputSchema(processOutputTable.schemas);
    return (
      isFeatureEnabled({feature: ProcessesFeature.PROCESSES_CREATE}) &&
      processOutputTable.metadata.tableLocation !== ProcessTableLocation.FIELD_MAPPING_MODAL &&
      !processOutputTable.metadata.disallowAddRows &&
      (outputSchema == null ||
        getProcessPlatformFeatureConfig(
          ProcessPlatformFeatureType.CAN_MANUALLY_CREATE_OUTPUTS,
          outputSchema.processExecutionType,
          outputSchema.processTaskSchemaName
        ))
    );
  }

  toTable(processOutputTable: ProcessOutputStructuredTable): Table {
    return {
      ...super.toTable(processOutputTable),
      lockedDims: true,
      allowAddRows: this._shouldAllowAddRows(processOutputTable),
    };
  }

  _getActionFunctionsForWorkspaceTab(
    tableMetadata: ProcessOutputStructuredTableMetadata,
    executionType?: ProcessExecutionType
  ): ActionFunctions<ProcessOutputStructuredTableMetadata, ProcessOutputStructuredTableRowMetadata> {
    const baseSecondaryActionFunctions = [
      createRequestV2SubmissionActionFunction,
      createProcessActionFunction,
      addToWorklistActionFunction,
      processTableInventoryShortcutsActionFunction,
    ];
    if (tableMetadata.tableLocation !== ProcessTableLocation.TASK_DETAILS_OUTPUTS_TAB) {
      baseSecondaryActionFunctions.push(openNextTaskGroupActionFunction);
    }
    const baseActionFunctions = {
      secondary: baseSecondaryActionFunctions,
      overflow: [openInDashboardActionFunction, downloadCSVActionFunction, printBarcodesActionFunction],
      contextMenu: [archiveOutputsActionFunction],
    };
    switch (executionType) {
      case ProcessExecutionType.DIRECT:
        return {
          ...baseActionFunctions,
          primary: [makeToggleEditModeActionFunction()],
          secondary:
            tableMetadata.tableLocation === ProcessTableLocation.TASK_DETAILS_OUTPUTS_TAB
              ? baseSecondaryActionFunctions
              : [...baseActionFunctions.secondary, taskFinderActionFunction],
        };
      case ProcessExecutionType.ENTRY:
        return baseActionFunctions;
      case ProcessExecutionType.FLOWCHART:
        return {
          ...baseActionFunctions,
          primary: [],
        };
      default:
        assert(false, `Unhandled execution type: ${executionType}`);
    }
  }

  getActionFunctions(
    tableMetadata: ProcessOutputStructuredTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<ProcessOutputStructuredTableMetadata, ProcessOutputStructuredTableRowMetadata> {
    const {tableLocation, isReadOnly} = tableMetadata;
    const outputSchema = getOutputSchema(schemas);
    const areFieldsEditable = outputSchema != null && canEditSchemaFieldValues(outputSchema);
    if (isReadOnly || outputSchema == null) {
      return {};
    }
    if (
      tableLocation === ProcessTableLocation.WORKSPACE_TAB ||
      tableLocation === ProcessTableLocation.TASK_DETAILS_OUTPUTS_TAB
    ) {
      if (isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT}) && areFieldsEditable) {
        return this._getActionFunctionsForWorkspaceTab(tableMetadata, outputSchema.processExecutionType);
      }
      return {
        secondary: [
          createRequestV2SubmissionActionFunction,
          createProcessActionFunction,
          addToWorklistActionFunction,
          processTableInventoryShortcutsActionFunction,
        ],
        overflow: [openInDashboardActionFunction, downloadCSVActionFunction, printBarcodesActionFunction],
      };
    } else if (tableLocation === ProcessTableLocation.ENTRY) {
      return {
        primary: [submitProcessOutputsActionFunction],
        secondary: [
          createRequestV2SubmissionActionFunction,
          createProcessActionFunction,
          addToWorklistActionFunction,
          taskFinderActionFunction,
          processTableInventoryShortcutsActionFunction,
          refreshTableActionFunction,
          openNextTaskGroupActionFunction,
        ],
        contextMenu: BenchlingConfig.ELN_ENABLE_ROW_BASED_SUBMISSION
          ? [submitProcessOutputsRowsActionFunction, archiveOutputsActionFunction]
          : [archiveOutputsActionFunction],
        overflow: [exportAuditLogsActionFunction, downloadCSVActionFunction, printBarcodesActionFunction],
      };
    } else if (tableLocation === ProcessTableLocation.CREATE_MODAL) {
      // Tables in the create modal are always read only, so they
      // get no additional action functions
      return {};
    } else if (tableLocation === ProcessTableLocation.DIRECT_EXECUTION_MODAL) {
      return {
        secondary: [taskFinderActionFunction, processTableInventoryShortcutsActionFunction],
      };
    } else if (tableLocation === ProcessTableLocation.STATUS_CONFIRMATION_MODAL) {
      // Tables in the status confirmation modal are read-only, so they get no
      // additional action functions
      return {};
    } else if (tableLocation === ProcessTableLocation.FIELD_MAPPING_MODAL) {
      return {};
    } else if (tableLocation === ProcessTableLocation.REQUEST_V2_SUBMISSION_DETAILS_PAGE) {
      return {overflow: [exportAuditLogsActionFunction, downloadCSVActionFunction]};
    }
    return super.getActionFunctions(tableMetadata, schemas);
  }

  getAddFromSourceOperations(
    entryId: string,
    structuredTable?: ProcessOutputStructuredTable
  ): Array<
    StructuredTableAddOperation<ProcessOutputStructuredTableMetadata, ProcessOutputStructuredTableRowMetadata>
  > {
    const tableMetadata = structuredTable?.metadata;
    const ops: Array<
      StructuredTableAddOperation<
        ProcessOutputStructuredTableMetadata,
        ProcessOutputStructuredTableRowMetadata
      >
    > = [];
    if (tableMetadata?.isReadOnly) {
      return ops;
    }
    if (
      tableMetadata?.tableLocation === ProcessTableLocation.CREATE_MODAL ||
      tableMetadata?.tableLocation === ProcessTableLocation.FIELD_MAPPING_MODAL
    ) {
      return ops;
    }
    if (entryId !== '') {
      ops.push({
        type: StructuredTableAddFromSourceType.ADD_FROM_TABLE,
        onSelect: ({note, structuredTable, addSamples}) => {
          addSamplesFromStructuredTable({
            destinationTable: structuredTable,
            adapter: this,
            note,
            entryId,
            addSamples,
          });
        },
      });
    }

    if (BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS) {
      ops.push({
        type: StructuredTableAddFromSourceType.ADD_FROM_WORKLIST,
        onSelect: ({structuredTable, addSamples}) => {
          addSamplesFromWorklist(
            structuredTable,
            this,
            addSamples,
            WORKLIST_FILL_LOCATION.PROCESS_OUTPUT_TABLE
          );
        },
      });
    }

    // Spreadsheet import
    ops.push({
      type: StructuredTableAddFromSourceType.ADD_FROM_SPREADSHEET,
      onSelect: async ({structuredTable, addFullRows}) => {
        const [processOutputSchema] = structuredTable.schemas;
        assert(
          processOutputSchema != null,
          'Tried to import spreadsheet into process output table with inaccessible or nonexistent schema'
        );
        const rows = await importRowsFromSpreadsheet({
          structuredTable,
          schemas: {processOutputSchema: processOutputSchema as ProcessOutputSchema},
          getSpreadsheetTypeForColumn: (column) =>
            getSpreadsheetTypeForColumn(ModelTypeConstants.PROCESS_OUTPUT_SCHEMA, column),
        });

        if (rows) {
          addFullRows(rows);
        }
      },
    });

    return ops;
  }

  getTableKindRowUpdater(): typeof ProcessOutputRowUpdater {
    return ProcessOutputRowUpdater;
  }

  getOutputId(
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ProcessOutputStructuredTableRowMetadata>
  ): string | null {
    const outputColumnIndex = this._getOutputColumnIndex(columns);
    return getProcessOutputIdFromTypedValue(row.cells[outputColumnIndex].typedValue);
  }

  getTaskId(
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ProcessOutputStructuredTableRowMetadata>
  ): string | null {
    const taskColumnIndex = this._getTaskColumnIndex(columns);
    return getProcessTaskIdFromCell(row.cells[taskColumnIndex]);
  }

  getAllTaskIds(
    columns: Array<StructuredTableColumn>,
    computedTable: Array<Array<EvaluatedCell>>
  ): Array<string> {
    const taskColumnIndex = this._getTaskColumnIndex(columns);
    return compact(computedTable.map((row) => getProcessTaskIdFromTypedValue(row[taskColumnIndex].value)));
  }

  setTaskCell(
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ProcessOutputStructuredTableRowMetadata>,
    taskCell: Cell
  ): StructuredTableRow<ProcessOutputStructuredTableRowMetadata> {
    const taskColumnIndex = this._getTaskColumnIndex(columns);
    const newCells = [...row.cells];
    newCells[taskColumnIndex] = taskCell;
    return {
      ...row,
      cells: newCells,
    };
  }

  getPrimarySamples(
    structuredTable: ProcessOutputStructuredTable,
    computedTable?: Array<Array<EvaluatedCell>> | null
  ): {[rowId: string]: Array<PrimarySample>} {
    const outputColumnIndex = this._getOutputColumnIndex(structuredTable.columns);
    const primarySamplesByRowId: {[rowId: string]: Array<PrimarySample>} = {};
    const schema = structuredTable.schemas[0];

    if (schema == null) {
      // Most likely, the user doesn't have permissions on the schema
      for (const {rowId} of structuredTable.rows) {
        primarySamplesByRowId[rowId] = [];
      }
      return primarySamplesByRowId;
    }

    structuredTable.rows.forEach(({rowId, cells}, rowIndex) => {
      const outputTypedValue =
        computedTable != null
          ? computedTable[rowIndex][outputColumnIndex]?.value
          : cells[outputColumnIndex].typedValue;
      const outputId = getProcessOutputIdFromTypedValue(outputTypedValue);
      if (outputId) {
        primarySamplesByRowId[rowId] = [
          {
            schemaId: schema.id,
            columnGroup: null,
            sampleLookupInfo: {sampleType: PrimarySampleLookupType.DIRECT, idForLookup: outputId},
          },
        ];
      } else {
        primarySamplesByRowId[rowId] = [];
      }
    });
    return primarySamplesByRowId;
  }

  getOutputSchema(structuredTable: ProcessOutputStructuredTable): ProcessOutputSchema | null {
    return getOutputSchema(structuredTable.schemas);
  }

  getColumnSummariesForCreateProcessShortcut(
    structuredTable: ProcessOutputStructuredTable
  ): Array<StructuredTableColumnSummary & {columnIndex: number}> {
    return getColumnSummariesForProcessRelatedTable(structuredTable);
  }

  allowPasteTable(): boolean {
    // Output tables in the notebook can only be inserted via process schema configuration.
    return false;
  }

  allowDeleteTable(structuredTable: ProcessOutputStructuredTable): {
    canDelete: boolean;
    reason?: string;
  } {
    // There is no way to re-insert output tables into an existing entry / template, so prevent deleting.
    return {canDelete: false};
  }

  allowMergedCellRange(
    structuredTable: ProcessOutputStructuredTable,
    column: StructuredTableColumn
  ): boolean {
    // merging cells is not allowed in Process Output tables
    return false;
  }
}
